/**
 * Generic form-control tools so the AI can fill in ordinary gateway forms,
 * not just the ones with explicit propose_* listeners. Reviewed resources
 * mark themselves in the DOM and fail closed here instead.
 *
 * Two tools:
 *   - `set_field({label, value})` — Find a labeled form field and set its
 *     value. Handles native <input>/<textarea>, the gateway's custom
 *     <Select> widget, and checkboxes/toggles.
 *   - `click_element({selector, containingText?})` — Click any button/link.
 *     Refuses to click submit buttons or anything inside a modal footer to
 *     keep the human-in-the-loop safety policy intact (the user always
 *     clicks Save/Connect/Submit themselves).
 *
 * Together with `propose_*` and `navigate_to`, these let the AI orchestrate
 * any setup flow: navigate → click "Add X" → fill each labeled field →
 * tell the user "form is ready, click Save when you're happy".
 */

const SUBMIT_DENY = new Set([
  "save",
  "submit",
  "connect",
  "create connection",
  "create",
  "add connection",
  "add api profile",
  "create profile",
  "delete",
  "remove",
  "confirm",
  "yes",
]);

// Some configuration surfaces have a purpose-built, reviewed AI proposal
// path. Generic DOM tooling must never become a second path around that
// review boundary. The page owns these markers; keeping the decision in the
// DOM also makes the protection apply to arbitrary selectors and labels.
const REVIEWED_OPCUA_RESOURCE_SELECTOR =
  '[data-gw-ai-form-control="reviewed-opcua"]';
const REVIEWED_EDGE_MODBUS_SERVER_SELECTOR =
  '[data-gw-ai-form-control="reviewed-edge-modbus-server"]';
const REVIEWED_PLC_DEPLOYMENT_SELECTOR =
  '[data-gw-ai-form-control="reviewed-plc-deployment"]';
const REVIEWED_OPCUA_MAPPING_SELECTOR =
  '[data-gw-ai-protected-field-group="opcua-mapping"]';
const CONFIGURED_ONLY_FIELD_SELECTOR =
  '[data-gw-ai-sensitive-field="configured-only"]';

const insideReviewedOpcuaResource = (element) =>
  !!element?.closest?.(REVIEWED_OPCUA_RESOURCE_SELECTOR);

const insideReviewedEdgeModbusServer = (element) =>
  !!element?.closest?.(REVIEWED_EDGE_MODBUS_SERVER_SELECTOR);

const insideReviewedPLCDeployment = (element) =>
  !!element?.closest?.(REVIEWED_PLC_DEPLOYMENT_SELECTOR);

const reviewedOpcuaRefusal = (action) => ({
  ok: false,
  error: {
    code: "REVIEWED_RESOURCE_PROTECTED",
    message:
      `Generic form tools cannot ${action} the reviewed OPC UA configuration. ` +
      "Use the purpose-built OPC UA proposal flow; a human must review and confirm it separately.",
  },
});

const reviewedEdgeModbusRefusal = (action) => ({
  ok: false,
  error: {
    code: "REVIEWED_RESOURCE_PROTECTED",
    message:
      `Generic form tools cannot ${action} the signed edge Modbus TCP server. ` +
      "Use propose_edge_modbus_tcp_server to open a draft; only a human may review and confirm its exact signed revision.",
  },
});

const reviewedPLCDeploymentRefusal = (action) => ({
  ok: false,
  error: {
    code: "REVIEWED_RESOURCE_PROTECTED",
    message:
      `Generic form tools cannot ${action} reviewed PLC deployment signing or fleet rollout controls. ` +
      "AI may inspect public status and prefill a proposal, but only the operator may build, confirm, pause, resume, cancel, roll back, or activate an exact reviewed artifact.",
  },
});

const cleanText = (s) =>
  String(s || "")
    .replace(/\s+/g, " ")
    .trim();

const lower = (s) => cleanText(s).toLowerCase();

const queryAll = (selector) => {
  try {
    return Array.from(document.querySelectorAll(selector));
  } catch {
    return [];
  }
};

/**
 * Native setter for <input>/<textarea>/<select> values, used to drive React
 * controlled inputs. React stamps a `_valueTracker` on each input so it can
 * detect "real" user input vs. programmatic — calling the native setter
 * resets the tracker and the dispatched event then runs through React's
 * normal change pipeline.
 */
function reactSetValue(el, value) {
  const proto =
    el instanceof HTMLTextAreaElement
      ? HTMLTextAreaElement.prototype
      : el instanceof HTMLSelectElement
        ? HTMLSelectElement.prototype
        : HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, "value")?.set;
  if (setter) setter.call(el, value);
  else el.value = value;
  el.dispatchEvent(new Event("input", { bubbles: true }));
  el.dispatchEvent(new Event("change", { bubbles: true }));
}

/**
 * Find the gateway "field" container (`<div class="gwField">`) whose label
 * text matches the requested label (case-insensitive substring match).
 */
function findFieldByLabel(labelText) {
  const target = lower(labelText);
  const fields = queryAll(".gwField");
  for (const field of fields) {
    const label = field.querySelector(".gwLabel, label");
    if (!label) continue;
    if (lower(label.textContent).includes(target)) return field;
  }
  // Fall back: any <label> whose text matches, then walk up to nearest
  // ancestor that contains a control.
  const labels = queryAll("label, .gwLabel");
  for (const label of labels) {
    if (!lower(label.textContent).includes(target)) continue;
    let parent = label.parentElement;
    while (parent && parent !== document.body) {
      if (parent.querySelector("input, textarea, select, .gwSelect-trigger")) {
        return parent;
      }
      parent = parent.parentElement;
    }
  }
  return null;
}

/**
 * Locate the actual control inside a field container.
 */
function findControl(field) {
  return (
    field.querySelector(
      "input[type='text'], input[type='email'], input[type='password'], input[type='number'], input[type='url'], input:not([type]), input[type='tel'], input[type='search']"
    ) ||
    field.querySelector("textarea") ||
    field.querySelector("select") ||
    field.querySelector(".gwSelect-trigger") ||
    field.querySelector(".tpInput") ||
    field.querySelector("input[type='checkbox']") ||
    field.querySelector("input")
  );
}

async function setSelectValue(triggerEl, valueOrLabel) {
  // Open the dropdown.
  triggerEl.click();
  // Wait for the menu to render.
  let menu = null;
  const deadline = Date.now() + 600;
  while (!menu && Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, 30));
    menu = document.querySelector(".gwSelect-menu");
  }
  if (!menu) {
    return { ok: false, error: "Couldn't open the select dropdown." };
  }
  const target = lower(valueOrLabel);
  const options = Array.from(menu.querySelectorAll(".gwSelect-option"));
  let match =
    options.find((o) => lower(o.textContent) === target) ||
    options.find((o) => lower(o.textContent).includes(target));
  if (!match) {
    // Close menu and report.
    triggerEl.click();
    const labels = options.map((o) => cleanText(o.textContent)).join(", ");
    return {
      ok: false,
      error: `No option matched "${valueOrLabel}". Available: ${labels}.`,
    };
  }
  match.click();
  return { ok: true };
}

/**
 * Classify a control element into the kinds the AI cares about. The kind
 * dictates what value shape `set_field` expects, so this is also the
 * grouping `list_form_fields` reports.
 */
function controlKind(control) {
  if (!control) return "unknown";
  if (control.classList?.contains("gwSelect-trigger")) return "select";
  if (control.classList?.contains("tpInput")) return "tag-picker";
  // ComboInput: typeable text input with a dropdown of preset suggestions.
  // Different from a pure text input — list_form_fields enumerates the
  // suggested options so the AI can pick one rather than guessing values.
  if (control.classList?.contains("gwComboInput")) return "combo";
  if (control.tagName === "TEXTAREA") return "textarea";
  if (control.tagName === "SELECT") return "select-native";
  if (control.tagName !== "INPUT") return "unknown";
  const t = (control.type || "text").toLowerCase();
  if (t === "checkbox") return "checkbox";
  if (t === "radio") return "radio";
  if (t === "number") return "number";
  if (t === "password") return "password";
  return "text";
}

/**
 * Briefly focus a ComboInput so its dropdown menu renders, then read the
 * option list and blur to close. The menu only mounts while `open` state
 * is true (driven by the input's focus). Returns options in the order the
 * widget shows them, with their group labels when present.
 */
async function readComboOptions(inputEl) {
  if (!inputEl) return [];
  const wrap = inputEl.closest(".gwComboWrap");
  if (!wrap) return [];
  const wasFocused = document.activeElement === inputEl;
  inputEl.focus();
  let menu = null;
  const deadline = Date.now() + 400;
  while (!menu && Date.now() < deadline) {
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, 25));
    menu = document.querySelector(".gwComboMenu");
  }
  const options = menu
    ? Array.from(menu.querySelectorAll(".gwComboOption")).map((opt) => {
        const labelEl = opt.querySelector(".gwComboOptionLabel");
        const valueEl = opt.querySelector(".gwComboOptionValue");
        const groupEl = opt
          .closest("div")
          ?.parentElement?.querySelector?.(".gwComboGroup");
        return {
          value: cleanText(valueEl?.textContent) || cleanText(labelEl?.textContent) || "",
          label: cleanText(labelEl?.textContent) || cleanText(valueEl?.textContent) || "",
          ...(groupEl ? { group: cleanText(groupEl.textContent) } : {}),
          active: opt.classList.contains("isActive"),
        };
      })
    : [];
  // Restore focus state so we don't disrupt the user.
  if (!wasFocused) inputEl.blur();
  return options;
}

function controlCurrentValue(control, kind) {
  if (!control) return null;
  if (kind === "checkbox") return !!control.checked;
  if (kind === "password") return null; // never expose
  if (kind === "select") {
    return cleanText(control.textContent) || null;
  }
  if (kind === "tag-picker") {
    return cleanText(control.querySelector(".tpInputValue")?.textContent) || "";
  }
  return control.value || "";
}

export const set_field = {
  name: "set_field",
  description:
    "Set the value of a labeled form field on the currently visible page. " +
    "Pass the field's visible **label** text and the value to enter. " +
    "**Value type matters and depends on the field kind:**\n" +
    "  - **Text / password / textarea / number / select**: pass a STRING " +
    "(the literal text to enter, e.g. \"gpt-4o-mini\", \"localhost\", \"5432\"). " +
    "Numbers may also be passed as JS numbers (e.g. 5432).\n" +
    "  - **Checkbox**: pass a BOOLEAN (`true` / `false`).\n" +
    "**NEVER** pass `true`/`false` to a text or select field — it would type the " +
    "literal word \"true\" into the input. If you don't know the field kind, call " +
    "`list_form_fields` first; it returns each ordinary field's kind and current value. " +
    "Reviewed OPC UA configuration refuses generic setters.",
  parameters: {
    type: "object",
    properties: {
      label: { type: "string", minLength: 1 },
      value: {},
    },
    required: ["label", "value"],
    additionalProperties: false,
  },
  execute: async ({ label, value }) => {
    if (typeof window === "undefined") {
      return { ok: false, error: { code: "NO_WINDOW", message: "Cannot run in non-browser context." } };
    }
    // Custom toggle widgets (`gwToggleRow` with `[role="switch"]`) live OUTSIDE
    // `.gwField`. `set_field` doesn't support them — these rows belong to
    // inline-form pages whose right edit path is the matching direct mutation
    // kind (e.g. update_workspace_settings runtimeAccess=public).
    const labelLower = lower(label);
    const toggleRow = Array.from(document.querySelectorAll(".gwToggleRow")).find(
      (row) => lower(row.querySelector(".gwLabel")?.textContent || "").includes(labelLower)
    );
    if (toggleRow) {
      return {
        ok: false,
        error: {
          code: "TOGGLE_NOT_SETTABLE",
          message:
            `"${label}" is a page-level toggle, not a regular form field. Don't use set_field — use the matching direct mutation kind in request_confirmation. For Gateway Settings (runtime access etc.) use \`update_workspace_settings\`. For Account Settings use \`update_my_profile\`. The toggle's current value is in list_form_fields output as kind:"toggle".`,
        },
      };
    }
    const field = findFieldByLabel(label);
    if (!field) {
      return {
        ok: false,
        error: {
          code: "FIELD_NOT_FOUND",
          message: `No form field labeled "${label}" is visible. Open the form first (use propose_* or click_element on New/Add).`,
        },
      };
    }
    if (insideReviewedOpcuaResource(field)) {
      return reviewedOpcuaRefusal(`change "${label}" in`);
    }
    if (insideReviewedEdgeModbusServer(field)) {
      return reviewedEdgeModbusRefusal(`change "${label}" in`);
    }
    if (insideReviewedPLCDeployment(field)) {
      return reviewedPLCDeploymentRefusal(`change "${label}" in`);
    }
    const control = findControl(field);
    if (!control) {
      return {
        ok: false,
        error: {
          code: "CONTROL_NOT_FOUND",
          message: `Found the "${label}" field but no input inside it.`,
        },
      };
    }

    // Custom Select trigger.
    if (control.classList?.contains("gwSelect-trigger")) {
      if (typeof value === "boolean") {
        return {
          ok: false,
          error: {
            code: "INVALID_VALUE",
            message: `Field "${label}" is a select dropdown — pass the option label or value (e.g. "PostgreSQL"), not a boolean.`,
          },
        };
      }
      const result = await setSelectValue(control, String(value));
      if (!result.ok) {
        return { ok: false, error: { code: "SELECT_FAILED", message: result.error } };
      }
      return { ok: true, data: { label, kind: "select", value } };
    }

    if (control.classList?.contains("tpInput")) {
      return {
        ok: false,
        error: {
          code: "TAG_PICKER_SELECTION_REQUIRED",
          message: `Field "${label}" only accepts an existing tag selected from its searchable tag picker.`,
        },
      };
    }

    // Checkbox.
    if (control.tagName === "INPUT" && control.type === "checkbox") {
      const desired = value === true || value === "true" || value === "yes" || value === 1 || value === "1";
      if (control.checked !== desired) control.click();
      return { ok: true, data: { label, kind: "checkbox", value: desired } };
    }

    // Native input/textarea/select. Reject obvious type mismatches up front
    // — the model occasionally emits `value: true` for a text field when it
    // means "yes, set this", which would otherwise type the literal word
    // "true" into the input. A clear error lets the model self-correct.
    if (typeof value === "boolean") {
      return {
        ok: false,
        error: {
          code: "INVALID_VALUE",
          message: `Field "${label}" is a ${control.tagName.toLowerCase()} input, not a checkbox — pass the literal text/number to enter (e.g. "gpt-4o-mini" or 5432), not a boolean.`,
        },
      };
    }
    if (value !== null && typeof value === "object") {
      return {
        ok: false,
        error: {
          code: "INVALID_VALUE",
          message: `Field "${label}" expects a primitive value (string or number), not an object.`,
        },
      };
    }
    const stringValue = value == null ? "" : String(value);
    try {
      reactSetValue(control, stringValue);
    } catch (err) {
      return {
        ok: false,
        error: { code: "SET_FAILED", message: err?.message || "Failed to set value." },
      };
    }
    return {
      ok: true,
      data: { label, kind: control.tagName.toLowerCase(), value: stringValue },
    };
  },
};

function isClickableSafe(el) {
  if (!el) return { safe: false, reason: "No element." };
  // Submit buttons → user only.
  if (el.tagName === "BUTTON" && (el.type || "submit") === "submit") {
    return { safe: false, reason: "won't click submit buttons — only the user submits forms." };
  }
  // Modal footer buttons → user only (Save/Cancel live there).
  if (el.closest && el.closest(".gwModalFooter")) {
    return {
      safe: false,
      reason: "won't click buttons inside a modal footer — those are Save/Cancel and only the user clicks them.",
    };
  }
  // Disabled.
  if (el.disabled || el.getAttribute("aria-disabled") === "true") {
    return { safe: false, reason: "the element is disabled." };
  }
  // Submit-y text.
  const txt = lower(el.textContent || el.getAttribute("aria-label") || "");
  if (txt && SUBMIT_DENY.has(txt)) {
    return {
      safe: false,
      reason: `won't click "${cleanText(el.textContent || el.getAttribute("aria-label"))}" — that looks like a submit/destructive action.`,
    };
  }
  return { safe: true };
}

export const click_element = {
  name: "click_element",
  description:
    "Click a button or link on the currently visible page. Use this to OPEN modals " +
    "(e.g. click `.gwPrimaryBtn` with text \"New Connection\"), expand sections, or " +
    "trigger non-destructive UI. Pass `selector` and optionally `containingText` to " +
    "narrow to the element whose visible text contains that string. " +
    "**Will refuse** to click submit buttons (`type=\"submit\"`), buttons in `.gwModalFooter`, " +
    "anything labeled Save/Submit/Connect/Create/Delete/Remove, or controls inside the reviewed " +
    "OPC UA configuration — only the USER uses that review flow.",
  parameters: {
    type: "object",
    properties: {
      selector: { type: "string", minLength: 1 },
      containingText: { type: "string" },
    },
    required: ["selector"],
    additionalProperties: false,
  },
  execute: async ({ selector, containingText }) => {
    if (typeof window === "undefined") {
      return { ok: false, error: { code: "NO_WINDOW", message: "Cannot run in non-browser context." } };
    }
    let candidates = queryAll(selector);
    if (containingText) {
      const t = lower(containingText);
      candidates = candidates.filter((el) => lower(el.textContent).includes(t));
    }
    if (candidates.length === 0) {
      return {
        ok: false,
        error: {
          code: "NOT_FOUND",
          message: `No element matched "${selector}"${containingText ? ` containing "${containingText}"` : ""}.`,
        },
      };
    }
    const el = candidates[0];
    if (insideReviewedOpcuaResource(el)) {
      return reviewedOpcuaRefusal("click controls inside");
    }
    if (insideReviewedEdgeModbusServer(el)) {
      return reviewedEdgeModbusRefusal("click controls inside");
    }
    if (insideReviewedPLCDeployment(el)) {
      return reviewedPLCDeploymentRefusal("click controls inside");
    }
    const safety = isClickableSafe(el);
    if (!safety.safe) {
      return {
        ok: false,
        error: { code: "REFUSED", message: safety.reason },
      };
    }
    try {
      el.click();
    } catch (err) {
      return {
        ok: false,
        error: { code: "CLICK_FAILED", message: err?.message || "Click failed." },
      };
    }
    return {
      ok: true,
      data: {
        clicked: cleanText(el.textContent) || el.getAttribute("aria-label") || el.tagName.toLowerCase(),
      },
    };
  },
};

/**
 * Batched form-fill — the cure for round-trip explosion. Filling a 5-field
 * form via individual `set_field` calls is 5 LLM rounds (and 5x the
 * latency); `set_fields` does it in one. Returns per-field results so the
 * model can see exactly which (if any) failed.
 */
export const set_fields = {
  name: "set_fields",
  description:
    "Fill multiple labeled form fields in a single tool call — much faster " +
    "than calling `set_field` once per field. Pass `fields[]`, each with " +
    "`{label, value}`. Same value-shape rules apply per field (string for " +
    "text/select/combo/textarea/number, boolean for checkboxes). Use this " +
    "whenever you have 2+ independent fields to set on the same form.\n" +
    "Returns `{results: [{label, ok, error?, value?}]}` so you can see " +
    "which fields landed and which need a different value.",
  parameters: {
    type: "object",
    properties: {
      fields: {
        type: "array",
        items: {
          type: "object",
          properties: {
            label: { type: "string", minLength: 1 },
            value: {},
          },
          required: ["label", "value"],
        },
      },
    },
    required: ["fields"],
    additionalProperties: false,
  },
  execute: async ({ fields }) => {
    if (!Array.isArray(fields) || fields.length === 0) {
      return {
        ok: false,
        error: { code: "INVALID_ARGS", message: "fields must be a non-empty array." },
      };
    }
    const results = [];
    let allOk = true;
    for (const f of fields) {
      // eslint-disable-next-line no-await-in-loop
      const r = await set_field.execute({ label: f.label, value: f.value });
      if (r.ok) {
        results.push({ label: f.label, ok: true, value: r.data?.value ?? f.value });
      } else {
        results.push({
          label: f.label,
          ok: false,
          error: r.error?.message || "set_field failed.",
        });
        allOk = false;
      }
    }
    return { ok: allOk, data: { results, count: results.length } };
  },
};

export const list_select_options = {
  name: "list_select_options",
  description:
    "Open a select/dropdown field by label and list its available options, " +
    "then close the dropdown. Use this BEFORE set_field on a select when " +
    "you don't already know the option labels — the gateway's selects are " +
    "dropdown-only (no free-text entry), so set_field will fail if you " +
    "guess an option that doesn't exist. Returns option labels and values. " +
    "Reviewed OPC UA configuration refuses generic option inspection.",
  parameters: {
    type: "object",
    properties: {
      label: { type: "string", minLength: 1 },
    },
    required: ["label"],
    additionalProperties: false,
  },
  execute: async ({ label }) => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return {
        ok: false,
        error: { code: "NO_WINDOW", message: "Cannot run in non-browser context." },
      };
    }
    const field = findFieldByLabel(label);
    if (!field) {
      return {
        ok: false,
        error: {
          code: "FIELD_NOT_FOUND",
          message: `No form field labeled "${label}" is visible. Open the form first.`,
        },
      };
    }
    if (insideReviewedOpcuaResource(field)) {
      return reviewedOpcuaRefusal(`inspect options for "${label}" in`);
    }
    if (insideReviewedEdgeModbusServer(field)) {
      return reviewedEdgeModbusRefusal(`inspect options for "${label}" in`);
    }
    if (insideReviewedPLCDeployment(field)) {
      return reviewedPLCDeploymentRefusal(`inspect options for "${label}" in`);
    }
    const trigger = field.querySelector(".gwSelect-trigger");
    if (!trigger) {
      return {
        ok: false,
        error: {
          code: "NOT_A_SELECT",
          message: `Field "${label}" is not a select/dropdown. Use list_form_fields to see its kind.`,
        },
      };
    }
    trigger.click();
    let menu = null;
    const deadline = Date.now() + 600;
    while (!menu && Date.now() < deadline) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 30));
      menu = document.querySelector(".gwSelect-menu");
    }
    if (!menu) {
      return {
        ok: false,
        error: { code: "OPEN_FAILED", message: "Couldn't open the dropdown to read options." },
      };
    }
    const options = Array.from(menu.querySelectorAll(".gwSelect-option")).map(
      (opt) => ({
        label: cleanText(opt.textContent),
        active: opt.classList.contains("isActive"),
      })
    );
    // Close the dropdown so we don't leave the UI in a half-open state.
    trigger.click();
    return {
      ok: true,
      data: { label, options, count: options.length },
    };
  },
};

export const list_form_fields = {
  name: "list_form_fields",
  description:
    "Snapshot of the currently visible form on the page — the AI's eyes " +
    "on the UI. Returns:\n" +
    "  - `modalTitle`/`modalSubtitle`: which modal is open, if any\n" +
    "  - `fields[]`: each labeled field with `label`, `kind` (text/select/" +
    "combo/checkbox/textarea/number/password), `currentValue`, `required`, " +
    "optional `placeholder`/`hint`/`disabled`. Reviewed OPC UA PKI references " +
    "return only `configured` plus `protected`, and namespace mapping fields " +
    "return protected metadata without `currentValue`.\n" +
    "  - `fields[].options[]`: for **combo** kinds (text input + suggestion " +
    "dropdown), the curated suggestions — `{value, label, group?}`. The " +
    "field still accepts any free-text value; the suggestions are the " +
    "form's curated picks (e.g. canonical model names per provider). " +
    "Prefer one of these unless the user explicitly wants something else.\n" +
    "  - `submitButton`/`cancelButton`: the modal footer buttons and their " +
    "disabled state (use `submitButton.disabled` to know if the form is " +
    "valid yet)\n" +
    "  - `tabs[]` (tabbed forms only): `[{ label, active }]` — one per tab " +
    "button. **Only the active tab's fields are in `fields[]`**. To reach " +
    "fields on another tab, call `click_element` to switch tabs then call " +
    "`list_form_fields` again.\n" +
    "  - `tabsNote` (tabbed forms only): a plain-English reminder of the " +
    "above with the exact `click_element` call to switch tabs.\n" +
    "Call this **before** `set_field` whenever you're unsure of a field's " +
    "kind or what values are valid — it stops you from passing a boolean " +
    "to a text input, AND stops you from inventing values like \"2024\" when " +
    "the form already curates a suggestion list. Empty fields list means " +
    "no form is open.",
  parameters: {
    type: "object",
    properties: {},
    additionalProperties: false,
  },
  execute: async () => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return {
        ok: false,
        error: { code: "NO_WINDOW", message: "Cannot run in non-browser context." },
      };
    }
    // Prefer a modal scope when one is visible — keeps us from picking up
    // unrelated .gwField elements elsewhere on the page.
    const modal = document.querySelector(".gwOverlay .gwModal") || document.querySelector(".gwModal");
    const scope = modal || document;

    // Inline-form pages (Gateway Settings, Account Settings) use `gwToggleRow`
    // for boolean controls — custom widgets, not inside `.gwField`. Surface
    // them here so the AI can SEE the toggle's current value even though the
    // right edit path is the page's direct mutation kind, not `set_field`.
    const toggleEls = Array.from(scope.querySelectorAll(".gwToggleRow"));
    const fields = [];
    for (const row of toggleEls) {
      const label = cleanText(row.querySelector(".gwLabel")?.textContent) || "(unlabeled toggle)";
      const hint = cleanText(row.querySelector(".gwHint")?.textContent) || null;
      const sw = row.querySelector('[role="switch"]');
      if (!sw) continue;
      const checked = sw.getAttribute("aria-checked") === "true";
      const disabled = sw.getAttribute("aria-disabled") === "true";
      fields.push({
        label,
        kind: "toggle",
        currentValue: checked,
        required: false,
        ...(hint ? { hint } : {}),
        ...(disabled ? { disabled: true } : {}),
        editVia: "page-level direct mutation (e.g. update_workspace_settings); set_field is not supported on toggles",
      });
    }

    const fieldEls = Array.from(scope.querySelectorAll(".gwField"));
    for (const field of fieldEls) {
      const labelEl = field.querySelector(".gwLabel, label");
      const label = cleanText(labelEl?.textContent) || "(unlabeled)";
      const control = findControl(field);
      if (!control) continue;
      const kind = controlKind(control);
      const required =
        control.required === true ||
        control.getAttribute?.("aria-required") === "true" ||
        /\*\s*$/.test(label) ||
        false;
      const placeholder =
        control.placeholder ||
        control.getAttribute?.("aria-placeholder") ||
        null;
      const hintEl = field.querySelector(".gwHint");
      const hint = hintEl ? cleanText(hintEl.textContent) : null;
      const configuredOnly = field.closest(CONFIGURED_ONLY_FIELD_SELECTOR);
      if (configuredOnly) {
        fields.push({
          label,
          kind: "sensitive-reference",
          configured:
            configuredOnly.getAttribute("data-gw-ai-configured") === "true",
          required,
          protected: true,
          editVia: "human-reviewed OPC UA configuration only",
        });
        continue;
      }
      if (field.closest(REVIEWED_OPCUA_MAPPING_SELECTOR)) {
        fields.push({
          label,
          kind,
          required,
          protected: true,
          editVia: "human-reviewed OPC UA namespace mapping only",
        });
        continue;
      }
      // For ComboInputs, enumerate the dropdown's suggested options so the
      // AI knows what's curated vs. what's free-text territory. The widget
      // accepts ANY value, but seeing the suggestions stops the AI from
      // inventing strings like "2024" when "gpt-4o-mini" was right there.
      let options;
      if (kind === "combo") {
        // eslint-disable-next-line no-await-in-loop
        options = await readComboOptions(control);
      }
      fields.push({
        label,
        kind,
        currentValue: controlCurrentValue(control, kind),
        required,
        ...(placeholder ? { placeholder } : {}),
        ...(hint ? { hint } : {}),
        ...(control.disabled ? { disabled: true } : {}),
        ...(options && options.length > 0 ? { options } : {}),
      });
    }

    let modalTitle = null;
    let modalSubtitle = null;
    let submitButton = null;
    let cancelButton = null;
    let pageType = "modal"; // or "inline"
    let pageSaveNote = null;
    if (modal) {
      modalTitle = cleanText(modal.querySelector(".gwModalTitle")?.textContent) || null;
      modalSubtitle =
        cleanText(modal.querySelector(".gwModalSubtitle")?.textContent) || null;
      const footer = modal.querySelector(".gwModalFooter");
      if (footer) {
        const btns = Array.from(footer.querySelectorAll("button"));
        const submit = btns.find((b) => (b.type || "submit") === "submit") || btns[btns.length - 1];
        const cancel = btns.find((b) => b !== submit && /cancel|close/i.test(b.textContent || ""));
        if (submit) {
          submitButton = {
            label: cleanText(submit.textContent) || "Submit",
            disabled: !!submit.disabled,
          };
        }
        if (cancel) {
          cancelButton = { label: cleanText(cancel.textContent) || "Cancel" };
        }
      }
    } else {
      // Inline-form page (no modal) — surface the page-level primary button
      // so the AI sees there IS a Save action and stops claiming "no form is
      // open." For these pages the AI must use the matching direct mutation
      // kind (e.g. update_workspace_settings, update_my_profile) rather than
      // `submit_open_form` — that tool only handles modals.
      const pagePrimary = document.querySelector(".gwFormStack .gwPageHeader .gwPrimaryBtn");
      if (pagePrimary) {
        pageType = "inline";
        submitButton = {
          label: cleanText(pagePrimary.textContent) || "Save",
          disabled: !!pagePrimary.disabled,
          inline: true,
        };
        pageSaveNote =
          "This is an inline-form page, not a modal. `submit_open_form` will NOT work here. Use the matching direct mutation kind for this page (e.g. `update_workspace_settings` for Gateway Settings, `update_my_profile` for Account Settings).";
      }
    }

    // Tab strip detection — scoped to the open modal so we don't pick up
    // unrelated tab UIs on the page.
    const tabButtons = Array.from(scope.querySelectorAll(".gwSubscriptionTab"));
    const tabs = tabButtons.length > 0
      ? tabButtons.map((btn) => ({
          label: cleanText(btn.textContent),
          active: btn.classList.contains("isActive"),
        }))
      : null;
    const tabsNote = tabs
      ? "This form has tabs. The fields above are ONLY from the active tab. To fill fields on another tab, call click_element({selector: \".gwSubscriptionTab\", containingText: \"<tab label>\"}) to switch, then call list_form_fields again."
      : null;

    return {
      ok: true,
      data: {
        modalTitle,
        modalSubtitle,
        fields,
        count: fields.length,
        submitButton,
        cancelButton,
        pageType,
        ...(pageSaveNote ? { pageSaveNote } : {}),
        ...(tabs ? { tabs, tabsNote } : {}),
      },
    };
  },
};

const formControlTools = [
  set_field,
  set_fields,
  click_element,
  list_form_fields,
  list_select_options,
];

export default formControlTools;
